import type { FC } from "react";
import { useEffect, useState } from "react";

import classNames from "classnames";

import { ChevronBackwardIcon } from "@jti/brand-icons/chevron-backward";
import { Button } from "@jti/core-components/components/Button";
import { Loader } from "@jti/core-components/components/Loader";
import type { FlotiqStorePrizeLocator } from "@jti/fe-common";
import { getUser, LoyaltyApi } from "@jti/rest-api";
import { type GetPrizesElementResponseType } from "@jti/rest-api-types";
import { DEPRECATED_IconButton } from "@jti/ui/components/DEPRECATED_IconButton";
import { VirtualPrizeBadges } from "@jti/ui/components/VirtualPrizeBadges";
import { usePagination } from "@jti/ui/hooks/usePagination/usePagination";
import { useTranslations } from "@jti/ui/providers/useTranslations";
import { replaceStringPlaceholder } from "@jti/ui/utils/replaceStringPlaceholder/replaceStringPlaceholder";
import { validateLimitedTimeBadgesData } from "@jti/ui/utils/validateBadgeData/validateBadgeData";

import { usePrizeLocatorContext } from "../../hooks";
import { GOOGLE_DIRECTION_URL_CONST } from "../../PrizeLocator";
import type { MobileDisplayType } from "../../PrizeLocator.types";
import { CardSensorialExperiance } from "../CardSensorialExperiance";

import styles from "./PrizesList.module.scss";

export type PrizesListProps = {
  onStoreSelect: (storeId: string) => void;
  stores?: FlotiqStorePrizeLocator[];
  activeStoreId?: string;
  displayType: MobileDisplayType;
};

export type VenueProps = {
  name: string;
  description: string;
  id: string;
  location: FlotiqStorePrizeLocator["Location"];
};

type PrizesState = { items: GetPrizesElementResponseType[]; totalItems: number };

enum LoadingStatus {
  INITIAL_LOAD,
  LOADING,
  SUCCESS,
  FAILED,
}

const validateTierRestriction = (tier?: string, levels?: string[]) => {
  return tier && levels?.length ? !levels.some((level) => level.toLowerCase() === tier.toLowerCase()) : false;
};

const isRewardAvailable = (
  isLogged: boolean,
  pointsBalance: number,
  rewardCost: number,
  tier: string,
  levels: string[],
  hasCoupons: boolean
) => isLogged && rewardCost <= pointsBalance && !validateTierRestriction(tier, levels) && hasCoupons;

const isLimitedOfferEnabled = (
  timeTo: string | undefined,
  oneDayLabel: string | undefined,
  daysLabel: string | undefined
) => {
  return !!timeTo && !!oneDayLabel && !!daysLabel;
};

export const PrizesList: FC<PrizesListProps> = ({ stores, activeStoreId, onStoreSelect, displayType }) => {
  const { __ } = useTranslations();
  const [prizes, setPrizes] = useState<PrizesState>({
    items: [],
    totalItems: 0,
  });
  const [prizePageNo, setPrizePageNo] = useState(1);
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.INITIAL_LOAD);
  const [isOLv4, setIsOLv4] = useState(false);
  const [prev, setPrev] = useState<{ pageNo?: number; activeStoreId?: string }>({
    activeStoreId: undefined,
    pageNo: undefined,
  });
  const {
    labels: {
      noOffersText,
      rewardsText,
      backToListLabel,
      loadMoreLabel,
      pointsLabel,
      newOfferDays,
      newLabel,
      oneDayLabel,
      daysLabel,
      outOfStockLabel,
      getDirectionLabel,
      redeemButtonLabel,
      discoverButtonText,
    },
    handleRedeemClick,
    userPointsBalance,
    pagination,
    tier,
    discoverUrlParent,
  } = usePrizeLocatorContext();

  const [paginatedItems, paginationLoadMore, paginationReset] = usePagination<GetPrizesElementResponseType>(
    prizes.items,
    pagination
  );

  const user = getUser();
  const isLogged = !!(user?.uuid && user?.ciam_token);
  const isMapView = displayType === "map";
  const activeStoreData = stores?.find((store) => store.ExternalId === activeStoreId);

  const fetchPrizes = () => {
    paginationReset();
    LoyaltyApi.init()
      .getPrizes({
        page: prizePageNo,
        perPage: pagination,
        sort: "asc",
        orderBy: "points",
        ...(activeStoreData?.ExternalId ? { location_ids: activeStoreData.ExternalId } : { is_virtual: "0" }),
      })
      .then((response) => {
        let newPrizes: PrizesState;
        if (loadingStatus === LoadingStatus.INITIAL_LOAD) {
          newPrizes = { items: [...response.items], totalItems: response.total };
          setIsOLv4(response.items.length === response.total);
        } else {
          newPrizes = { ...prizes, items: [...prizes.items, ...response.items] };
        }
        setPrizes(newPrizes);
        setLoadingStatus(LoadingStatus.SUCCESS);
      })
      .catch((response) => {
        setLoadingStatus(LoadingStatus.FAILED);
        console.error(response);
      });
  };

  if (prev.pageNo !== prizePageNo && prizePageNo > 1) {
    setPrev({ ...prev, pageNo: prizePageNo });
    setLoadingStatus(LoadingStatus.LOADING);
  }

  if (prev.activeStoreId !== activeStoreData?.ExternalId) {
    setPrev({ ...prev, activeStoreId: activeStoreData?.ExternalId });
    setPrizePageNo(1);
    setLoadingStatus(LoadingStatus.INITIAL_LOAD);
  }

  useEffect(() => {
    if (loadingStatus === LoadingStatus.INITIAL_LOAD || loadingStatus === LoadingStatus.LOADING) fetchPrizes();
  }, [loadingStatus]);

  //fixme after full migration to OLv5 code below neeeds to be removed;
  const prizesToDisplay = isOLv4 ? { items: paginatedItems, totalItems: prizes.totalItems } : { ...prizes };
  const handleLoadMore = () => {
    if (isOLv4) {
      paginationLoadMore();
    } else {
      setPrizePageNo(prizePageNo + 1);
    }
  };
  const handleBackToListClick = () => {
    onStoreSelect("");
  };

  const prizesCount = replaceStringPlaceholder(
    rewardsText,
    [{ placeholder: "count", value: prizes.totalItems }],
    false
  );

  const getGetDirectionUrl = () => {
    if (activeStoreData) {
      return `${GOOGLE_DIRECTION_URL_CONST}${activeStoreData?.Location?.lat},${activeStoreData?.Location?.lon}`;
    }
  };

  return (
    <div
      className={styles.wrapper}
      data-testid="storeLocatorCardList"
      id="storeLocatorCardList"
      data-prizescount={prizes.totalItems}
    >
      {loadingStatus === LoadingStatus.INITIAL_LOAD ? (
        <div className={styles.loader}>
          <Loader data-testid="prizeListLoader" />
        </div>
      ) : (
        <div className={styles.prizesList}>
          {activeStoreData && (
            <div
              className={classNames(styles.container, {
                [styles.mapView]: isMapView,
              })}
            >
              <DEPRECATED_IconButton
                data-testid={"prizeListBackButton"}
                color="tertiary"
                onClick={handleBackToListClick}
                className={styles.backButton}
                icon={ChevronBackwardIcon}
              >
                {backToListLabel}
              </DEPRECATED_IconButton>
              <h2 className={styles.venueName}>{activeStoreData.StoreName}</h2>
              <span className={styles.avaliablePrizes}>{prizesCount}</span>
              <p className={styles.description}>{activeStoreData.Description}</p>
            </div>
          )}
          {!prizesToDisplay.items?.length ? (
            <h2 className={styles.noOffers}>{noOffersText}</h2>
          ) : (
            <>
              <div data-testid="listItems" className={styles.container}>
                {prizesToDisplay.items?.map((item) => {
                  const discoverButtonUrl =
                    discoverUrlParent && item.slug ? `${discoverUrlParent}/${item.slug}` : undefined;
                  const hasLimitedOfferBadge = isLimitedOfferEnabled(item.time_to, oneDayLabel, daysLabel);
                  const isTierRestricted = validateTierRestriction(tier, item.levels);
                  const { isNewBadgeVisible, isMoreThanOneDay, limitedTimeOfferLabel } = validateLimitedTimeBadgesData({
                    __,
                    newOfferDays,
                    timeFrom: item.time_from,
                    timeTo: item.time_to,
                    daysLabel,
                    oneDayLabel,
                  });
                  const lowestEligibleTier = item.levels?.[0];
                  const color = item.color === "black" ? "black" : "white";
                  return (
                    <CardSensorialExperiance
                      discoverButtonUrl={discoverButtonUrl}
                      discoverButtonText={discoverButtonText}
                      title={item.name ?? ""}
                      getDirectionLabel={getDirectionLabel}
                      redeemButtonLabel={redeemButtonLabel}
                      linkLabel={item.brandName ?? ""}
                      key={item.prize_uuid}
                      description={item.subtitle ?? ""}
                      getDirectionUrl={getGetDirectionUrl() ?? ""}
                      imageUrl={item.images?.[0] ?? ""}
                      points={`${item.points} ${pointsLabel}`}
                      isAvailable={isRewardAvailable(
                        isLogged,
                        userPointsBalance,
                        item.points,
                        tier,
                        item.levels ?? [],
                        item.has_coupons
                      )}
                      handleRedeemClick={() => handleRedeemClick(item)}
                      handleVenueClick={() => onStoreSelect(item.locations?.[0] ?? "")}
                    >
                      <VirtualPrizeBadges
                        showNewBadge={isNewBadgeVisible}
                        isTierRestricted={isTierRestricted || !isLogged}
                        tier={lowestEligibleTier ?? ""}
                        isOutOfStock={item.has_coupons === false}
                        hasLimitedOfferBadge={hasLimitedOfferBadge}
                        newBadgeLabel={newLabel}
                        timeLimitedBadgeLabel={limitedTimeOfferLabel}
                        isMoreThanOneDay={isMoreThanOneDay}
                        color={color}
                        outOfStockLabel={outOfStockLabel ?? ""}
                        isPlacedInSmallBox={true}
                      />
                    </CardSensorialExperiance>
                  );
                })}
              </div>
              {prizes.totalItems > prizesToDisplay.items?.length &&
                (loadingStatus === LoadingStatus.LOADING ? (
                  <Loader />
                ) : (
                  <Button onClick={handleLoadMore} variant="secondary">
                    {loadMoreLabel}
                  </Button>
                ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};
